// Time utility functions

export const timeSlots = [
  '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00',
  '17:00', '18:00', '19:00', '20:00', '21:00', '22:00', '23:00'
];

// Generate all available time options (hourly and half-hourly)
export const getAllTimeOptions = () => {
  const options = [];
  for (let hour = 9; hour <= 23; hour++) {
    // Add hourly option
    options.push(`${hour.toString().padStart(2, '0')}:00`);
    // Add half-hourly option (except for 23:30 which would be past midnight)
    if (hour < 23) {
      options.push(`${hour.toString().padStart(2, '0')}:30`);
    }
  }
  return options;
};

export const daysOfWeek = [
  'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'
];

export const getTimeSlotIndex = (time) => {
  return timeSlots.indexOf(time);
};

export const getDurationInSlots = (durationMinutes) => {
  // Convert minutes to number of 1-hour slots
  return durationMinutes / 60;
};

// Check if a time is a half-hour time (e.g., 09:30, 10:30)
export const isHalfHourTime = (time) => {
  return time.endsWith(':30');
};

// Get the base hour slot for a time (e.g., 09:30 -> 09:00, 10:00 -> 10:00)
export const getBaseHourSlot = (time) => {
  const [hours] = time.split(':');
  return `${hours}:00`;
};

// Get the half-hour time for a base hour slot (e.g., 09:00 -> 09:30)
export const getHalfHourTime = (time) => {
  const [hours] = time.split(':');
  return `${hours}:30`;
};

// Calculate the vertical offset for half-hour times (in pixels)
// Returns 0 for hourly times, or half the slot height for half-hour times
export const getHalfHourOffset = (time, slotHeight) => {
  if (isHalfHourTime(time)) {
    return slotHeight / 2;
  }
  return 0;
};

export const getStartSlotIndex = (startTime) => {
  const index = timeSlots.indexOf(startTime);
  return index !== -1 ? index : 0;
};

export const getEndSlotIndex = (startTime, durationMinutes) => {
  const startIndex = getStartSlotIndex(startTime);
  const slots = getDurationInSlots(durationMinutes);
  return startIndex + slots;
};

export const formatTime = (timeString) => {
  // Convert "14:00" to "2:00 PM"
  const [hours, minutes] = timeString.split(':');
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minutes} ${ampm}`;
};

export const isWeekend = (day) => {
  return day === 'Saturday' || day === 'Sunday';
};

export const isWeekdayMorning = (time) => {
  const hour = parseInt(time.split(':')[0], 10);
  return hour >= 9 && hour <= 17; // 9 AM to 5 PM (17:00)
};

// Week utility functions for Monday-Sunday weeks
export const getMondayOfWeek = (date = new Date()) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
  return new Date(d.setDate(diff));
};

export const getSundayOfWeek = (date = new Date()) => {
  const monday = getMondayOfWeek(date);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return sunday;
};

export const getWeekRange = (date = new Date()) => {
  const monday = getMondayOfWeek(date);
  const sunday = getSundayOfWeek(date);
  return { monday, sunday };
};

export const formatWeekRange = (monday, sunday) => {
  const formatDate = (date) => {
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const day = date.getDate();
    return `${month} ${day}`;
  };
  
  if (monday.getMonth() === sunday.getMonth()) {
    return `${formatDate(monday)}–${sunday.getDate()}`;
  } else {
    return `${formatDate(monday)}–${formatDate(sunday)}`;
  }
};

export const isDateInWeek = (date, weekMonday) => {
  const weekSunday = new Date(weekMonday);
  weekSunday.setDate(weekMonday.getDate() + 6);
  weekSunday.setHours(23, 59, 59, 999); // End of Sunday
  
  const checkDate = new Date(date);
  checkDate.setHours(0, 0, 0, 0);
  const weekStart = new Date(weekMonday);
  weekStart.setHours(0, 0, 0, 0);
  
  return checkDate >= weekStart && checkDate <= weekSunday;
};

export const getAllWeeksFromEarliestDate = (earliestDate) => {
  const weeks = [];
  const startDate = getMondayOfWeek(earliestDate);
  const today = new Date();
  const currentWeekMonday = getMondayOfWeek(today);
  
  let weekMonday = new Date(startDate);
  
  while (weekMonday <= currentWeekMonday) {
    const weekSunday = new Date(weekMonday);
    weekSunday.setDate(weekMonday.getDate() + 6);
    
    weeks.push({
      monday: new Date(weekMonday),
      sunday: weekSunday,
      label: formatWeekRange(weekMonday, weekSunday),
    });
    
    // Move to next week
    weekMonday.setDate(weekMonday.getDate() + 7);
  }
  
  return weeks.reverse(); // Most recent week first
};

